<template>
  <template v-if="isAuthenticated && upsertedUser && user">
    <Toast />
    <Unauthorized v-if="!isAuthorized" email="" />
    <EulaCheck v-else />
  </template>
  <UILoadingScreen v-else :loading="true" loadingMessage="Loading..." />
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import type {
  CustomUserClaimValue,
  CustomUserClaims
} from "@okta/okta-auth-js";
import { createUpsertClient, mainUrqlConfig, upsertUser } from "@/urql";
import useUserInfo, { SpyglassRoles } from "@/composables/auth/useUserInfo";
import { useAuth } from "@okta/okta-vue";
import { useOnline } from "@vueuse/core";
import Unauthorized from "@/components/Unauthorized.vue";
import { useLogger } from "@/composables/logger";
import { refreshAuthTokens } from "@/utils/authentication";
import { minutesToMilliseconds } from "date-fns";
import { FALLBACK_OKTA_AUTH_REFRESH_MINUTES } from "@/libs/calculations/constants";
import { provideClient, type Operation } from "@urql/vue";
import { getLocalStorageArray } from "@/utils/utils-dependent";
import EulaCheck from "@/components/EulaCheck.vue";
import Toast from "primevue/toast";
import UILoadingScreen from "@/components/UILoadingScreen.vue";

const { info, error } = useLogger("AUTH PROVIDER");
const urqlClient = provideClient(mainUrqlConfig());

const user = useUserInfo();
const isOnline = useOnline();
const authClient = useAuth();

const isAuthenticated = ref(false);
const isAuthorized = ref(false);
const upsertedUser = ref(false);

// will only return an access token if it exists and has not expired.
const getValidAccessToken = () => {
  const accessToken = authClient.authStateManager.getAuthState()?.accessToken;
  if (accessToken && !authClient.tokenManager.hasExpired(accessToken)) {
    return accessToken;
  }
  return null;
};

// used to determine if we're in a state where we can successfully retry cached operations
const shouldRetryCachedOperations = computed(() => {
  return isOnline.value && isAuthenticated.value && isAuthorized.value;
});

watch(shouldRetryCachedOperations, () => {
  // I've found no good way to get reactivity for this accessToken, so doing a check inside this watch is a crucial,
  // if there is no valid accessToken the user will wind up having to refresh the page to validate it and this watcher
  // will get called again but this time having a valid accessToken.
  const accessToken = getValidAccessToken();
  if (shouldRetryCachedOperations.value && accessToken) {
    // starts process of kicking off all cached operations if an item exists inside the local storage array
    const cachedOfflineOperation: Operation =
      getLocalStorageArray("offline").shift();
    if (cachedOfflineOperation) {
      info(
        "User went back online and is authenticated, start retrying cached offline operation",
        cachedOfflineOperation.key
      );
      // re-executes the operation -> this will wind up setting off a chain reaction inside offlineRetryExchange.ts
      // as when this operation completed, it will see that it was in the local storage array, remove it, and then continue on
      // with the next cached operation.
      urqlClient.reexecuteOperation(cachedOfflineOperation);
    }
  }
});

const reauthIntervalId = ref<ReturnType<typeof setInterval> | null>(null);

handlePageLoad();

async function startReauthenticationInterval() {
  if (reauthIntervalId.value !== null) {
    clearInterval(reauthIntervalId.value);
  }
  reauthIntervalId.value = setInterval(
    async () => {
      const authState = authClient.authStateManager.getAuthState();
      if (
        isOnline.value &&
        authState?.refreshToken &&
        !authClient.tokenManager.hasExpired(authState.refreshToken)
      ) {
        await refreshAuthTokens(authClient);
      }
    },
    minutesToMilliseconds(
      parseInt(window?.config?.OKTA_AUTH_REFRESH_MINUTES) ||
        FALLBACK_OKTA_AUTH_REFRESH_MINUTES
    )
  );
}

async function handlePageLoad() {
  const authState = authClient.authStateManager.getAuthState();

  startReauthenticationInterval();

  // if we're offline we don't care about auth expiration at this point and just want to go with
  // whatever state they were the last time they loaded the page.
  if (!isOnline.value) {
    isAuthorized.value = !!authState?.idToken;
    isAuthenticated.value = !!authState?.isAuthenticated;
    return;
  }

  // checks our auth state for an id token to determine if we're authorized
  isAuthorized.value = !!authState?.idToken?.claims?.identifier || false;

  // use okta's isAuthenticated method with the renew option passed so it will automatically refresh token expiration if we're authenticated
  isAuthenticated.value = await authClient.isAuthenticated({
    onExpiredToken: "renew"
  });

  if (isAuthenticated.value) {
    try {
      if (
        authState?.refreshToken &&
        !authClient.tokenManager.hasExpired(authState.refreshToken)
      ) {
        // we were authenticated and our refresh token is not expired, so also explicitly renew all tokens
        const newTokens = await refreshAuthTokens(authClient);

        // recheck authorization, either we already were authorized, or now we are if identifier exists in the new tokens
        isAuthorized.value =
          isAuthorized.value || !!newTokens.idToken?.claims?.identifier;
      }
    } catch (err) {
      return;
    }
  }

  // Upsert User with temp urql client
  const accessToken = authClient.getAccessToken();
  const upsertClient = createUpsertClient(accessToken);
  const user = await buildUser();

  if (user && upsertClient) {
    await upsertUser(upsertClient, user);
    upsertedUser.value = true;
    info("User updated");
  } else {
    if (!user) {
      error("User not found");
    }
    if (!upsertClient) {
      error("Upsert Client not found");
    }
  }
}

async function buildUser(): Promise<CustomUserClaims> {
  const authState = await authClient.authStateManager.updateAuthState();
  const id_claims = authState.idToken?.claims;
  const halIdent = id_claims?.identifier as CustomUserClaimValue;
  const access_claims = authState.accessToken?.claims || {
    "x-hasura-default-role": SpyglassRoles.registered_user
  };

  return {
    ...id_claims,
    id: id_claims?.sub || "",
    hal_identifier: halIdent,
    role: access_claims?.["x-hasura-default-role"]
  };
}
</script>

<style lang="scss" scoped>
h1 {
  font-size: 30px;
}
</style>
